/** Which screens are reached from another, and from where — the derivation behind the breadcrumb.
 *
 * Pure and in its own file so it is tested without a router, and so `Breadcrumbs.tsx` exports only
 * the component (fast refresh).
 *
 * **Derived from the address, not remembered.** A trail built from the history of clicks is wrong
 * after a refresh and wrong in a shared link, which is exactly the case the URL contract
 * (`qa/KEEP.md` item 2) exists to make right.
 */

import { FILTER_KEYS } from "../screens/search";
import type { TrailPlace } from "./navigationTrail";
import type { RecordPlace } from "./routeTable";

export interface Crumb {
  to: string;
  label: string;
  /** The `?tab=` of the parent, when the parent is one tab of a path's tabs (Processing's Missing). */
  tab?: string;
  /** The parent's own search, carried verbatim (the list a record was opened from, with its filters). */
  search?: Record<string, unknown>;
}

/** What a record page knows about where it sits, which its address cannot say (U7). */
export interface RecordTrail {
  /** The place the page says it belongs to (`recordPlace`). */
  lights?: RecordPlace | undefined;
  /** The last list the reader opened a record from, with its filters (`recordOrigin`). */
  origin?: TrailPlace | undefined;
}

/** How many filters a list's search carries: the register's own and a table's (`f_*`, `fq`). */
export function filtersKept(search: Record<string, unknown>): number {
  const named = (key: string) => {
    const value = search[key];
    return typeof value === "string" ? value !== "" : value !== undefined && value !== null;
  };
  return (
    FILTER_KEYS.filter(named).length +
    Object.keys(search).filter((key) => (key.startsWith("f_") || key === "fq") && named(key)).length
  );
}

/** The parent of a record page: **the list the reader opened it from** (so Back keeps the filters
 * and the window that list had; EXP-12), else **the place the record belongs to** (its tab's list),
 * else, with neither, the inventory's register, which is the only guess left. It is never a fixed
 * "All emissions": a Scope 2 record, a document and a filing each say their own. */
function recordParent(
  labelOf: (to: string) => string | undefined,
  trail: RecordTrail | undefined,
): Crumb {
  const origin = trail?.origin;
  if (origin) {
    const kept = filtersKept(origin.search);
    const word = labelOf(origin.pathname) ?? "the list";
    return {
      to: origin.pathname,
      label: kept > 0 ? `${word} · ${String(kept)} filter${kept === 1 ? "" : "s"} kept` : word,
      search: origin.search,
    };
  }
  const place = trail?.lights;
  if (place) {
    const [path = "/", tab] = place.tab.split("?tab=");
    return { to: path, label: labelOf(path) ?? path, ...(tab ? { tab } : {}) };
  }
  // The register is a tab of the inventory screen, and the reader came from its rows.
  return { to: "/emissions", label: labelOf("/emissions") ?? "/emissions", tab: "register" };
}

/** One screen's parents, outermost first. `label` is omitted for a destination the rail names. */
interface Rule {
  match: RegExp;
  parents: (found: RegExpMatchArray) => Array<{ to: string; label?: string }>;
}

const RULES: readonly Rule[] = [
  // A record is opened from a list, whichever kind it is: `parentsOf` answers it before these rules,
  // from the page's own place and the list it was opened from (`recordParent`).
  { match: /^\/evidence\/([^/]+)$/, parents: () => [{ to: "/documents" }] },
  // Detail pages (Batch 5): each is opened from the register that lists what it describes.
  { match: /^\/risks\/[^/]+$/, parents: () => [{ to: "/risks" }] },
  { match: /^\/people\/[^/]+$/, parents: () => [{ to: "/people" }] },
  { match: /^\/ledger\/[^/]+$/, parents: () => [{ to: "/ledger" }] },
  // Batch 5's detail pages: a supplier is opened from the exchange, a factor from the library.
  { match: /^\/suppliers\/[^/]+$/, parents: () => [{ to: "/vendors" }] },
  { match: /^\/factors\/[^/]+$/, parents: () => [{ to: "/factors" }] },
  {
    match: /^\/evidence\/([^/]+)\/import$/,
    parents: (found) => [{ to: "/documents" }, { to: `/evidence/${found[1]}`, label: "Review" }],
  },
  // Reached from Facilities (ADR-0091; it was the Organisation screen's Sites panel).
  { match: /^\/organisation\/structure$/, parents: () => [{ to: "/facilities" }] },
  // Reached from the person's menu, which is on every screen — so the way back is Home.
  { match: /^\/(profile|my-settings|settings)$/, parents: () => [{ to: "/", label: "Home" }] },
];

/** The parents of a screen, or `null` when it is not reached from another.
 *
 * Pure and exported so the derivation is tested without a router. `labelOf` resolves a rail
 * destination's name from the rail's own list, so a label is written once: a second copy would be
 * the one that goes stale when a destination is renamed.
 */
export function parentsOf(
  pathname: string,
  labelOf: (to: string) => string | undefined,
  record?: RecordTrail,
): Crumb[] | null {
  if (/^\/records\/[^/]+$/.test(pathname)) return [recordParent(labelOf, record)];
  for (const rule of RULES) {
    const found = pathname.match(rule.match);
    if (!found) continue;
    return rule.parents(found).map((parent) => ({
      to: parent.to,
      label: parent.label ?? labelOf(parent.to) ?? parent.to,
    }));
  }
  return null;
}
